import type {
  GradingDiffFactor,
  GradingQuestion,
  GradingRole,
  GradingRoleCluster,
  GradingTreePayload,
} from '../admin/grading/types';
import { matchRoleByScore } from '../admin/recordGrading/gradingEngine';
import type { MatrixSelections } from '../../../services/localEmployeeGradingStore';

export type MatrixCellOption = {
  answerId: number;
  answerText: string | null;
  matrixDescription: string | null;
  answerScore: number;
};

export type MatrixCell = {
  options: MatrixCellOption[];
};

export function matrixOptionDisplayText(option: MatrixCellOption): string | null {
  const description = option.matrixDescription?.trim();
  return description || null;
}

export type MatrixColumn = {
  roleCluster: GradingRoleCluster;
  label: string;
  roleNames: string[];
};

/** Одна строка матрицы: диффактор + индекс вопроса внутри кластера (0, 1, …). */
export type MatrixRow = {
  factor: GradingDiffFactor;
  questionIndex: number;
  questionsInFactor: number;
};

export type GradingMatrixModel = {
  factors: GradingDiffFactor[];
  rows: MatrixRow[];
  columns: MatrixColumn[];
  cells: Record<string, MatrixCell>;
};

export function matrixSelectionKey(factorId: number, questionIndex: number): string {
  return `${factorId}:${questionIndex}`;
}

export function matrixCellKey(factorId: number, questionIndex: number, roleClusterId: number): string {
  return `${factorId}:${questionIndex}:${roleClusterId}`;
}

function isEntityVisible(value?: boolean): boolean {
  return value !== false;
}

function questionHasVisibleAnswers(questionId: number, tree: GradingTreePayload): boolean {
  return tree.answers.some(
    (answer) => answer.question_id === questionId && isEntityVisible(answer.is_visible),
  );
}

function questionsForFactorCluster(
  factorId: number,
  roleClusterId: number,
  tree: GradingTreePayload,
): GradingQuestion[] {
  return tree.questions
    .filter(
      (q) => q.diff_factor_id === factorId
        && q.role_cluster_id === roleClusterId
        && isEntityVisible(q.is_visible)
        && questionHasVisibleAnswers(q.id, tree),
    )
    .sort((a, b) => a.id - b.id);
}

export function factorHasQuestionCoverage(
  factorId: number,
  familyId: number,
  tree: GradingTreePayload,
): boolean {
  const clusterIds = new Set(
    tree.roleClusters.filter((rc) => rc.family_id === familyId).map((rc) => rc.id),
  );
  return tree.questions.some(
    (q) => q.diff_factor_id === factorId
      && clusterIds.has(q.role_cluster_id)
      && isEntityVisible(q.is_visible)
      && questionHasVisibleAnswers(q.id, tree),
  );
}

export function roleClustersForFamily(familyId: number, tree: GradingTreePayload): GradingRoleCluster[] {
  return tree.roleClusters
    .filter((rc) => rc.family_id === familyId && isEntityVisible(rc.is_visible))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function factorsForFamily(familyId: number, tree: GradingTreePayload): GradingDiffFactor[] {
  return tree.factors
    .filter((f) => f.family_id === familyId && isEntityVisible(f.is_visible))
    .filter((f) => factorHasQuestionCoverage(f.id, familyId, tree))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function rolesForFamily(familyId: number, tree: GradingTreePayload): GradingRole[] {
  const clusterIds = new Set(
    tree.roleClusters.filter((rc) => rc.family_id === familyId).map((rc) => rc.id),
  );
  return tree.roles
    .filter((r) => clusterIds.has(r.role_cluster_id))
    .sort((a, b) => {
      const rcA = tree.roleClusters.find((rc) => rc.id === a.role_cluster_id);
      const rcB = tree.roleClusters.find((rc) => rc.id === b.role_cluster_id);
      const rcNameA = rcA?.name ?? '';
      const rcNameB = rcB?.name ?? '';
      if (rcNameA !== rcNameB) return rcNameA.localeCompare(rcNameB);
      if (a.grade_level !== b.grade_level) return a.grade_level - b.grade_level;
      return a.name.localeCompare(b.name);
    });
}

export function rolesForRoleCluster(roleClusterId: number, tree: GradingTreePayload): GradingRole[] {
  return tree.roles
    .filter((r) => r.role_cluster_id === roleClusterId)
    .sort((a, b) => a.grade_level - b.grade_level || a.name.localeCompare(b.name));
}

function sortedAnswersByQuestion(tree: GradingTreePayload): Map<number, typeof tree.answers> {
  const map = new Map<number, typeof tree.answers>();
  for (const answer of tree.answers) {
    const list = map.get(answer.question_id) ?? [];
    list.push(answer);
    map.set(answer.question_id, list);
  }
  map.forEach((list) => {
    list.sort((a, b) => a.answer_score - b.answer_score || a.id - b.id);
  });
  return map;
}

function maxQuestionsForFactor(
  factorId: number,
  clusterIds: number[],
  tree: GradingTreePayload,
): number {
  let max = 0;
  for (const clusterId of clusterIds) {
    max = Math.max(max, questionsForFactorCluster(factorId, clusterId, tree).length);
  }
  return max;
}

function buildMatrixRows(
  familyId: number,
  tree: GradingTreePayload,
  factors: GradingDiffFactor[],
): MatrixRow[] {
  const clusterIds = tree.roleClusters
    .filter((rc) => rc.family_id === familyId)
    .map((rc) => rc.id);

  const rows: MatrixRow[] = [];

  for (const factor of factors) {
    const questionsInFactor = maxQuestionsForFactor(factor.id, clusterIds, tree);
    for (let questionIndex = 0; questionIndex < questionsInFactor; questionIndex += 1) {
      rows.push({ factor, questionIndex, questionsInFactor });
    }
  }

  return rows;
}

function buildMatrixCells(
  tree: GradingTreePayload,
  rows: MatrixRow[],
  columns: MatrixColumn[],
): Record<string, MatrixCell> {
  const cells: Record<string, MatrixCell> = {};
  const answersByQuestion = sortedAnswersByQuestion(tree);

  for (const row of rows) {
    for (const col of columns) {
      const key = matrixCellKey(row.factor.id, row.questionIndex, col.roleCluster.id);
      const clusterQuestions = questionsForFactorCluster(
        row.factor.id,
        col.roleCluster.id,
        tree,
      );
      const question = clusterQuestions[row.questionIndex];

      if (!question) {
        cells[key] = { options: [] };
        continue;
      }

      const sortedAnswers = answersByQuestion.get(question.id) ?? [];
      cells[key] = {
        options: sortedAnswers
          .filter((answer) => isEntityVisible(answer.is_visible))
          .map((answer) => ({
            answerId: answer.id,
            answerText: answer.answer_text,
            matrixDescription: answer.matrix_description ?? null,
            answerScore: answer.answer_score,
          })),
      };
    }
  }

  return cells;
}

export function buildGradingMatrix(
  familyId: number,
  tree: GradingTreePayload,
): GradingMatrixModel {
  const factors = factorsForFamily(familyId, tree);
  const rows = buildMatrixRows(familyId, tree, factors);
  const clusters = roleClustersForFamily(familyId, tree);
  const columns: MatrixColumn[] = clusters.map((roleCluster) => ({
    roleCluster,
    label: roleCluster.name,
    roleNames: rolesForRoleCluster(roleCluster.id, tree)
      .filter((role) => isEntityVisible(role.is_visible))
      .map((role) => role.name),
  }));

  return {
    factors,
    rows,
    columns,
    cells: buildMatrixCells(tree, rows, columns),
  };
}

export function inferRoleClusterFromSelections(
  selections: MatrixSelections,
  tree: GradingTreePayload,
): number | null {
  const counts = new Map<number, number>();
  for (const answerId of Object.values(selections)) {
    const answer = tree.answers.find((a) => a.id === answerId);
    if (!answer) continue;
    const question = tree.questions.find((q) => q.id === answer.question_id);
    if (!question) continue;
    counts.set(question.role_cluster_id, (counts.get(question.role_cluster_id) ?? 0) + 1);
  }

  let bestClusterId: number | null = null;
  let bestCount = 0;
  counts.forEach((count, clusterId) => {
    if (count > bestCount) {
      bestCount = count;
      bestClusterId = clusterId;
    }
  });
  return bestClusterId;
}

export function totalScoreFromSelections(
  selections: MatrixSelections,
  tree: GradingTreePayload,
): number {
  return Object.values(selections).reduce((sum, answerId) => {
    const answer = tree.answers.find((a) => a.id === answerId);
    return sum + (answer?.answer_score ?? 0);
  }, 0);
}

export function resolveGradingFromSelections(
  selections: MatrixSelections,
  tree: GradingTreePayload,
  specializationId: number | null = null,
): GradingRole | null {
  const roleClusterId = inferRoleClusterFromSelections(selections, tree);
  if (!roleClusterId) return null;
  const totalScore = totalScoreFromSelections(selections, tree);
  return matchRoleByScore(tree.roles, roleClusterId, specializationId, totalScore) ?? null;
}

function parseSelectionKey(key: string): { factorId: number; questionIndex: number } | null {
  const match = /^(\d+):(\d+)$/.exec(key);
  if (!match) return null;
  return { factorId: Number(match[1]), questionIndex: Number(match[2]) };
}

export function migrateSelectionsIfNeeded(
  selections: MatrixSelections,
  matrix: GradingMatrixModel,
  tree: GradingTreePayload,
): MatrixSelections {
  const rowKeys = new Set(
    matrix.rows.map((row) => matrixSelectionKey(row.factor.id, row.questionIndex)),
  );
  const keys = Object.keys(selections);
  if (keys.length === 0) return selections;
  if (keys.every((key) => rowKeys.has(key))) return selections;

  const answerIds = new Set(tree.answers.map((a) => a.id));
  const migrated: MatrixSelections = {};

  for (const [keyStr, answerId] of Object.entries(selections)) {
    if (!answerIds.has(answerId)) continue;

    if (rowKeys.has(keyStr)) {
      migrated[keyStr] = answerId;
      continue;
    }

    const parsed = parseSelectionKey(keyStr);
    if (parsed && rowKeys.has(keyStr)) {
      migrated[keyStr] = answerId;
      continue;
    }

    const numericKey = Number(keyStr);
    if (Number.isFinite(numericKey)) {
      const answer = tree.answers.find((a) => a.id === answerId);
      if (answer) {
        const question = tree.questions.find((q) => q.id === answer.question_id);
        if (question) {
          const clusterQuestions = questionsForFactorCluster(
            question.diff_factor_id,
            question.role_cluster_id,
            tree,
          );
          const questionIndex = clusterQuestions.findIndex((q) => q.id === question.id);
          if (questionIndex >= 0) {
            const newKey = matrixSelectionKey(question.diff_factor_id, questionIndex);
            if (rowKeys.has(newKey)) {
              migrated[newKey] = answerId;
              continue;
            }
          }
        }
      }

      const legacyKey = matrixSelectionKey(numericKey, 0);
      if (rowKeys.has(legacyKey)) {
        migrated[legacyKey] = answerId;
      }
    }
  }

  return migrated;
}

export function rowAppliesToCluster(
  row: MatrixRow,
  roleClusterId: number,
  matrix: GradingMatrixModel,
): boolean {
  const cell = matrix.cells[matrixCellKey(row.factor.id, row.questionIndex, roleClusterId)];
  return (cell?.options.length ?? 0) > 0;
}

/** Строки, которые нужно заполнить с учётом кластера, выведенного из текущих ответов. */
export function getRequiredRowKeys(
  matrix: GradingMatrixModel,
  selections: MatrixSelections,
  tree: GradingTreePayload,
): string[] {
  const inferredCluster = inferRoleClusterFromSelections(selections, tree);
  if (inferredCluster == null) return [];

  return matrix.rows
    .filter((row) => rowAppliesToCluster(row, inferredCluster, matrix))
    .map(matrixRowKey);
}

export function isMatrixComplete(
  selections: MatrixSelections,
  matrix: GradingMatrixModel,
  tree: GradingTreePayload,
): boolean {
  if (Object.keys(selections).length === 0) return false;

  const inferredCluster = inferRoleClusterFromSelections(selections, tree);
  if (inferredCluster == null) return false;

  const requiredKeys = getRequiredRowKeys(matrix, selections, tree);
  if (requiredKeys.length === 0) return false;

  return requiredKeys.every((rowKey) => selections[rowKey] != null);
}

export function formatGradingResult(role: Pick<GradingRole, 'name'>): string {
  return role.name;
}

export function matrixRowLabel(row: MatrixRow): string {
  if (row.questionsInFactor <= 1) return row.factor.name;
  return row.factor.name;
}

export function matrixRowKey(row: MatrixRow): string {
  return matrixSelectionKey(row.factor.id, row.questionIndex);
}
